import { ensureErrorWithMessage } from './ensureErrorWithMessage';

class RequestError extends Error {
  constructor(response: Response) {
    super(
      `${response.status} ${response.statusText}, an error occurred while fetching the data`,
    );
    this.status = response.status;
  }

  info: any;
  status: number;

  // Attach extra info to the error object.
  async extractInfo(response: Response) {
    try {
      const json = await response.json();
      this.info = json;

      if (this.info?.message) {
        this.message = this.info.message;
      }
    } catch {
      // Do Nothing
    }
  }
}

function isEmptyResponse(response: Response) {
  const contentType = response.headers.get('content-type');
  return (
    response.status === 204 ||
    (contentType != null && contentType.includes('text/html'))
  );
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: Record<string, unknown>;
};

export async function extraInfoRequest<TResponse>(
  path: string,
  { method = 'GET', body, headers, ...fetchArgs }: RequestOptions = {},
  basePath = '/api/checkout',
) {
  const response = await fetch(`${basePath}/${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
    ...fetchArgs,
  });

  // If the status code is not in the range 200-299,
  // we still try to parse and throw it.
  if (!response.ok) {
    const error = new RequestError(response);
    await error.extractInfo(response);
    throw error;
  }

  let data: TResponse;
  if (isEmptyResponse(response)) {
    data = {} as TResponse;
  } else {
    try {
      data = await response.json();
    } catch (error) {
      throw ensureErrorWithMessage(error);
    }
  }

  return {
    res: response,
    status: response.status,
    statusText: response.statusText,
    data,
  };
}

export default async function request<TResponse = {}>(
  path: string,
  options?: RequestOptions,
  basePath?: string,
) {
  const result = await extraInfoRequest<TResponse>(path, options, basePath);
  return result.data;
}

export function createFetcher<TResponse>(basePath: string) {
  return async function fetcher(path: string, options?: RequestOptions) {
    return request<TResponse>(path, options, basePath);
  };
}

export const defaultFetcher = request;
